import { paramCase, pascalCase } from 'change-case';
import cloneDeep from 'lodash/cloneDeep.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import isEmpty from 'lodash/isEmpty.js';
import merge from 'lodash/merge.js';
import pluralize from 'pluralize';

import Objective from '@/models/Objective.js';
import ObjectiveCriteria from '@/models/ObjectiveCriteria.js';
import axios from '@/plugins/axios.js';
import RecursiveSearch from '@/utilities/RecursiveSearch.js';

export default class CourseFormHelpers {
    constructor(store) {
        this.store = store;
        this.state = this.store.state;
        this.getters = this.store.getters;
        this.dispatch = this.store.dispatch;
        this.commit = this.store.commit;
        this.rootState = this.store.rootState;
        this.rootGetters = this.store.rootGetters;

        this.original = null;
        this.cloned = null;
        this.template = null;
        this.activity = null;
        this.request = null;

        this.search = new RecursiveSearch();
        this.targetParentHistory = [];
        this.selected = [];
    }

    resolveNamespace(item) {
        if (item?.namespace) {
            return item.namespace;
        }

        if (item?.type) {
            return pluralize(pascalCase(item.type));
        }

        return null;
    }

    assignClonedProperties(activity, id) {
        let payload;

        if (activity) {
            payload = this.assignActivityProperties(id);
        }

        if (!activity) {
            payload = this.assignSectionProperties(id);
        }

        payload.displayStatus = { editing: false };

        return payload;
    }

    assignActivityProperties(id) {
        const activityKey = (() => {
            if (this.original?.remediation)
                return this.template ? 'remediation_activity_templates' : 'remediation_activities';

            return this.template ? 'activity_templates' : 'activities';
        })();

        let activityIndex = this.request.data[activityKey].findIndex(item => item.uuid === id);

        if (activityIndex === -1 && this.request?.cloned?.id) {
            activityIndex = this.request.data[activityKey].findIndex(item => item.id === this.request.cloned.id);
        }

        const payload = this.request.data[activityKey][activityIndex];

        if (!payload) {
            throw new Error(`Duplicated activity [${id}] was missing from the duplicate response.`);
        }

        if (!has(payload, 'activity_type')) payload.activity_type = payload.activity_type_key;

        if (typeof payload?.activity_type === 'object' && payload?.activity_type !== null)
            payload.activity_type = payload.activity_type.key;

        return payload;
    }

    assignSectionProperties(id) {
        // eslint-disable-next-line camelcase
        const section_key = this.template ? 'section_templates' : 'sections';
        // eslint-disable-next-line camelcase
        const section_index = this.request.data[section_key].findIndex(item => item.uuid === id);

        // eslint-disable-next-line camelcase
        return this.request.data[section_key][section_index];
    }

    async checkForNestedItems() {
        const search = new RecursiveSearch();

        const items = await search.get(this.request.data.items, {
            targetKey: 'uuid',
            container: 'items',
            subjectKey: this.cloned.uuid,
            field: 'items',
        });

        if (!isEmpty(items)) {
            search.do(items, 'items', async item => {
                const activity = item.type.includes('act');
                const namespace = (() => {
                    if (this.template) {
                        return activity ? 'ActivityTemplates' : 'SectionTemplates';
                    }
                    return activity ? 'Activities' : 'Sections';
                })();

                const payload = this.assignClonedProperties(activity, item.uuid);

                await this.hydrateClonedItem(namespace, payload);
            });
        }

        return items;
    }

    async duplicate(item) {
        const namespace = this.resolveNamespace(item);

        if (!namespace) {
            throw new Error('Unable to resolve namespace for duplicated course item.');
        }

        this.original = merge(
            this.rootGetters[`${namespace}/getByKey`]({
                key: Number(item.id),
                keyName: 'id',
                field: '',
            }),
            item,
            { namespace }
        );

        this.template = this.original.type.includes('temp');
        this.activity = this.original.type.includes('act');

        this.request = await axios.post(`/api/${paramCase(namespace)}/${item.id}/duplicate`).then(resp => resp.data);

        this.cloned = this.assignClonedProperties(this.activity, this.request.cloned.uuid);

        const items = item?.remediation ? [] : await this.checkForNestedItems();

        await this.hydrateClonedItem(namespace, this.cloned);

        const payload = {
            id: this.cloned.id,
            order: this.cloned.order,
            type: this.original.type,
            title: this.cloned.title,
            uuid: this.cloned.uuid,
            namespace,
            // Ensure newly duplicated items start expanded in the UI
            isLocalCollapsed: false,
            isCollapsed: false,
        };

        this.hydrateObjectives();

        if (!this.activity) {
            const settingsKey = this.template ? 'section_settings_template' : 'section_settings';

            payload[settingsKey] = this.cloned[settingsKey];

            payload.items = [];

            if (items) {
                payload.items = items;
            }
        }

        // Use the original's parent to decide placement; the API payload for the clone
        // doesn't include a `parent` field (it uses *_id), and hydration maps that later.
        if (get(this.original, 'parent')) {
            payload.parent = this.original.parent;
            this.commit('addSectionItem', payload);
        } else if (!item?.remediation) {
            this.commit('insertDuplicate', payload);
        }

        if (!item?.remediation) this.commit('reIndex');
    }

    async hydrateObjectives() {
        const sectionKey = this.template ? 'section_templates' : 'sections';
        const sectionIndex = this.request.data[sectionKey].findIndex(item => item.id === this.request.cloned.id);
        const originalIndex = this.request.data[sectionKey].findIndex(item => item.id === this.original.id);

        if (this.request.data[sectionKey][originalIndex] || this.request.data[sectionKey][0].id === this.original.id) {
            const originalObjective = this.request.data[sectionKey][originalIndex]
                ? this.request.data[sectionKey][originalIndex]
                : this.request.data[sectionKey][0];

            const objective = this.template ? originalObjective.objective_template : originalObjective.objective;

            if (objective) {
                await Objective.insert({
                    data: {
                        courseable_id: objective.courseable_id,
                        courseable_type: objective.courseable_type,
                        subject_id: this.request.data[sectionKey][sectionIndex].id,
                        subject_type: objective.subject_type,
                        title: objective.title,
                        heading: objective.heading,
                        description: objective.description,
                    },
                }).then(async response => {
                    objective.criterias.map(async criteria => {
                        await ObjectiveCriteria.insert({
                            data: {
                                objective_id: response.objectives[0].id,
                                order: criteria.order,
                                description: criteria.description,
                            },
                        });
                    });

                    await response.objectives[0].save();
                });
            }
        }
    }

    async hydrateClonedItem(namespace, payload) {
        // eslint-disable-next-line camelcase
        const hydrate_type = namespace.includes('Act') ? 'Activity' : 'Section';
        // eslint-disable-next-line camelcase
        const hydrate_module = `${namespace}/hydrate${hydrate_type}`;

        await this.dispatch(hydrate_module, { payload }, { root: true });
    }

    getPrerequisites(uuid) {
        const items = cloneDeep(this.getters.courseItem('items'));

        this.setupParentHistory(uuid, items);

        this.buildOptionList(uuid, items);

        if (this.currentWeekNumber != null) {
            // Disable options from future weeks relative to current item
            this.search.do(items, 'children', item => {
                if (item.candidateWeekNumber != null && item.candidateWeekNumber > this.currentWeekNumber) {
                    item.isDisabled = true;
                }
            });
        }

        this.disableParents(items);

        this.disableChildren(items, uuid);

        if (this.selected.length) {
            this.search.do(items, 'children', item => {
                // eslint-disable-next-line id-denylist
                this.selected.forEach(obj => {
                    // eslint-disable-next-line id-denylist
                    if (item.id === obj.id && uuid === obj.prereq) {
                        item.isDisabled = true;
                    }
                });
            });
        }

        return items;
    }

    setupParentHistory(target, container) {
        container.forEach(item => {
            if (has(item, 'items')) {
                if (!isEmpty(item.items)) {
                    item.items.forEach(nestedItem => {
                        if (has(item, 'parentHistory')) {
                            nestedItem.parentHistory = [...item.parentHistory];

                            nestedItem.parentHistory.push(item.uuid);
                        } else {
                            nestedItem.parentHistory = [item.uuid];
                        }
                    });

                    this.setupParentHistory(target, item.items);
                }
            }
        });
    }

    buildOptionList(uuid, items) {
        // eslint-disable-next-line complexity
        this.search.do(items, 'children', item => {
            const namespace = pluralize(pascalCase(item.type));

            const isObjective = item.type === 'objective';

            const flatItem = !isObjective
                ? this.rootGetters[`${namespace}/get`](item.uuid)
                : this.rootGetters['SectionTemplates/get'](uuid) || this.rootGetters['Sections/get'](uuid);

            if (!flatItem) return;

            const template = flatItem.type.includes('temp');
            const settingsKey = template ? 'section_settings_template' : 'section_settings';
            const section = flatItem.type.includes('sec');

            function getField(field) {
                return flatItem[field];
            }

            const hasPrereq = getField('prerequisite_uuid');
            const hasChildren = has(item, 'items');
            const hasParent = has(item, 'parent');
            const title = getField('title');
            const self = item.uuid === uuid;

            if (hasPrereq) this.selected.push({ id: item.uuid, prereq: hasPrereq });

            item.label = title;

            if (section && get(flatItem, 'sectionType') === 'week') {
                item.label = `Week ${flatItem[settingsKey].week_number}`;

                if (title) {
                    item.label = `${item.label}: ${title}`;
                }
            }

            item.id = item.uuid;

            if (hasParent) delete item.parent;

            if (self) {
                item.isDisabled = true;

                if (has(item, 'parentHistory')) {
                    this.targetParentHistory = [...item.parentHistory];
                }

                this.currentWeekNumber = this.getWeekNumberForFlatItem(flatItem);
            }

            if (hasChildren) {
                item.children = item.items;
            }

            item.candidateWeekNumber = this.getWeekNumberForFlatItem(flatItem);
        });
    }

    disableParents(container) {
        container.forEach(item => {
            if (this.targetParentHistory.includes(item.uuid)) {
                item.isDisabled = true;
            }

            if (!isEmpty(item.items)) {
                this.disableParents(item.items);
            }
        });
    }

    disableChildren(container, targetUuid) {
        container.forEach(item => {
            // If an item has a parentHistory and it includes the target,
            // then this item is a descendant of the current target and must be disabled.
            if (
                has(item, 'parentHistory') &&
                Array.isArray(item.parentHistory) &&
                item.parentHistory.includes(targetUuid)
            ) {
                item.isDisabled = true;
            }

            if (!isEmpty(item.items)) {
                this.disableChildren(item.items, targetUuid);
            }
        });
    }

    /**
     * Get the week number for a given item using store lookups.
     * @param {Object} flatItem - The item to get the week number for.
     * @returns {Number|null} The week number for the item, or null if not found.
     */
    getWeekNumberForFlatItem(flatItem) {
        const extractWeekNumber = (sectionLike, settingsKey) => {
            if (!sectionLike || sectionLike.sectionType !== 'week') return null;

            const weekNumber = get(sectionLike, `[${settingsKey}].week_number`);

            return weekNumber !== undefined ? Number(weekNumber) : null;
        };

        const findAncestorWeekNumber = (startItem, sectionModule, settingsKey) => {
            let current = startItem;
            const visitedIds = new Set();

            while (current && get(current, 'parent_id')) {
                const currentId = current.id;

                if (visitedIds.has(currentId)) break;

                visitedIds.add(currentId);

                const parent = this.rootGetters[`${sectionModule}/getByKey`]({
                    keyName: 'id',
                    key: Number(get(current, 'parent_id')),
                    field: null,
                });

                if (!parent) break;

                const directWeek = extractWeekNumber(parent, settingsKey);

                if (directWeek !== null) return directWeek;

                current = parent;
            }

            return null;
        };

        try {
            if (!flatItem || typeof flatItem !== 'object') return null;

            const type = flatItem.type || '';
            const isTemplate = type.includes('temp');
            const settingsKey = isTemplate ? 'section_settings_template' : 'section_settings';
            const sectionModule = isTemplate ? 'SectionTemplates' : 'Sections';

            if (type.includes('sec')) {
                const directWeek = extractWeekNumber(flatItem, settingsKey);

                if (directWeek !== null) return directWeek;

                return findAncestorWeekNumber(flatItem, sectionModule, settingsKey);
            }

            if (type.includes('act')) {
                const sectionIdKey = isTemplate ? 'section_template_id' : 'section_id';
                const sectionId = get(flatItem, sectionIdKey);

                if (!sectionId) return null;

                const section = this.rootGetters[`${sectionModule}/getByKey`]({
                    keyName: 'id',
                    key: Number(sectionId),
                    field: null,
                });

                if (!section) return null;

                const directWeek = extractWeekNumber(section, settingsKey);

                if (directWeek !== null) return directWeek;

                return findAncestorWeekNumber(section, sectionModule, settingsKey);
            }

            return null;
        } catch (_e) {
            return null;
        }
    }
}
